import { createRoutine } from 'redux-saga-routines'
import { call, put, takeLatest, all, select, fork } from 'redux-saga/effects'
import { toast } from 'react-toastify'
import * as inboxService from 'services/inboxService'
import { getApiErrors } from 'utils/errors'

// Routines
export const getEmailsListRoutine = createRoutine('GET_EMAILS_LIST')
export const getEmailStatsRoutine = createRoutine('GET_EMAIL_STATS')
export const getEmailDetailsRoutine = createRoutine('GET_EMAIL_DETAILS')
export const markEmailAsReadRoutine = createRoutine('MARK_EMAIL_AS_READ')
export const retryEmailAttachmentsRoutine = createRoutine('RETRY_EMAIL_ATTACHMENTS')
export const retryEmailAttachmentRoutine = createRoutine('RETRY_EMAIL_ATTACHMENT')
export const refreshInboxRoutine = createRoutine('REFRESH_INBOX')

// Saga workers
function* getEmailsList({ payload }) {
  yield put(getEmailsListRoutine.request())
  
  try {
    const { data } = yield call(inboxService.getEmailsList, payload)
    yield put(getEmailsListRoutine.success({
      emails: data.data,
      meta: data.meta,
      append: payload.append || false
    }))
  } catch (error) {
    const errorMessage = getApiErrors(error)
    yield put(getEmailsListRoutine.failure(error))
    console.error('Error fetching emails:', errorMessage)
  }
}

function* getEmailStats({ payload }) {
  yield put(getEmailStatsRoutine.request())
  
  try {
    const { data } = yield call(inboxService.getEmailStats, payload)
    yield put(getEmailStatsRoutine.success(data.data))
  } catch (error) {
    const errorMessage = getApiErrors(error)
    yield put(getEmailStatsRoutine.failure(error))
    console.error('Error fetching email stats:', errorMessage)
  }
}

function* getEmailDetails({ payload }) {
  yield put(getEmailDetailsRoutine.request())
  
  try {
    const { data } = yield call(inboxService.getEmailDetails, payload)
    yield put(getEmailDetailsRoutine.success(data.data))
  } catch (error) {
    const errorMessage = getApiErrors(error)
    yield put(getEmailDetailsRoutine.failure(error))
    console.error('Error fetching email details:', errorMessage)
  }
}

function* markEmailAsRead({ payload }) {
  yield put(markEmailAsReadRoutine.request())
  
  try {
    yield call(inboxService.markEmailAsRead, payload)
    yield put(markEmailAsReadRoutine.success(payload.emailId))
    
    // Refresh stats after marking as read
    yield put(getEmailStatsRoutine.trigger({ bpId: payload.bpId }))
  } catch (error) {
    const errorMessage = getApiErrors(error)
    yield put(markEmailAsReadRoutine.failure(error))
    console.error('Error marking email as read:', errorMessage)
  }
}

function* retryEmailAttachments({ payload }) {
  yield put(retryEmailAttachmentsRoutine.request())
  
  try {
    const { data } = yield call(inboxService.retryEmailAttachments, payload)
    yield put(retryEmailAttachmentsRoutine.success(payload.emailId))
    
    toast.success('All attachments queued for retry')
    
    // Refresh the email list to update attachment statuses
    yield put(refreshInboxRoutine.trigger({ bpId: payload.bpId }))
  } catch (error) {
    const errorMessage = getApiErrors(error)
    yield put(retryEmailAttachmentsRoutine.failure(error))
    toast.error(errorMessage || 'Failed to retry attachments')
  }
}

function* retryEmailAttachment({ payload }) {
  yield put(retryEmailAttachmentRoutine.request())
  
  try {
    const { data } = yield call(inboxService.retryEmailAttachment, payload)
    yield put(retryEmailAttachmentRoutine.success({
      emailId: payload.emailId,
      attachmentId: payload.attachmentId
    }))
    
    toast.success('Attachment queued for retry')
    
    // Refresh the email list to update attachment status
    yield put(refreshInboxRoutine.trigger({ bpId: payload.bpId }))
  } catch (error) {
    const errorMessage = getApiErrors(error)
    yield put(retryEmailAttachmentRoutine.failure(error))
    toast.error(errorMessage || 'Failed to retry attachment')
  }
}

function* refreshInbox({ payload }) {
  try {
    yield put(refreshInboxRoutine.request());
    
    // Fetch both emails list and stats directly
    yield all([
      call(getEmailsList, { 
        payload: {
          bpId: payload.bpId, 
          page: 1, 
          append: false 
        }
      }),
      call(getEmailStats, { payload: { bpId: payload.bpId } })
    ]);
    
    yield put(refreshInboxRoutine.success());
  } catch (error) {
    console.error('Error in refreshInbox:', error);
    yield put(refreshInboxRoutine.failure(error));
  }
}

// Watchers
export function* getEmailsListWatcher() {
  yield takeLatest(getEmailsListRoutine.TRIGGER, getEmailsList)
}

export function* getEmailStatsWatcher() {
  yield takeLatest(getEmailStatsRoutine.TRIGGER, getEmailStats)
}

export function* markEmailAsReadWatcher() {
  yield takeLatest(markEmailAsReadRoutine.TRIGGER, markEmailAsRead)
}

export function* retryEmailAttachmentsWatcher() {
  yield takeLatest(retryEmailAttachmentsRoutine.TRIGGER, retryEmailAttachments)
}

export function* retryEmailAttachmentWatcher() {
  yield takeLatest(retryEmailAttachmentRoutine.TRIGGER, retryEmailAttachment)
}

export function* getEmailDetailsWatcher() {
  yield takeLatest(getEmailDetailsRoutine.TRIGGER, getEmailDetails)
}

export function* refreshInboxWatcher() {
  yield takeLatest(refreshInboxRoutine.TRIGGER, refreshInbox)
}

// Export all sagas
export const inboxSagas = [
  fork(getEmailsListWatcher),
  fork(getEmailStatsWatcher),
  fork(getEmailDetailsWatcher),
  fork(markEmailAsReadWatcher),
  fork(retryEmailAttachmentsWatcher),
  fork(retryEmailAttachmentWatcher),
  fork(refreshInboxWatcher)
]